import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import CommandCenter from './CommandCenter';
import RemediationDashboard from './RemediationDashboard';
import CpaClientWorkspace from './CpaClientWorkspace';
import './Marketplace.css';
import './CpaDashboard.css';
import { isSoc2Name } from '../../utils/tsc';

const FRAMEWORKS = [
  'SOC 2', 'GDPR', 'ISO 27001', 'NIST CSF', 'PCI DSS', 'HIPAA', 'COBIT 5', 'ITIL',
];

const INV_STATUS_COLORS = {
  PAID:    { bg: '#d1fae5', text: '#065f46' },
  PENDING: { bg: '#fef3c7', text: '#92400e' },
  OVERDUE: { bg: '#fee2e2', text: '#991b1b' },
  VOID:    { bg: '#f1f5f9', text: '#64748b' },
};

const SVC_LABELS = {
  SUBSCRIPTION: i18n.t('CpaDashboard.svcSubscription'),
  AUDIT_FEE:    i18n.t('CpaDashboard.svcAuditFee'),
  COMMISSION:   i18n.t('CpaDashboard.svcCommission'),
};
const SVC_COLORS = {
  SUBSCRIPTION: { bg: '#dbeafe', text: '#1e40af' },
  AUDIT_FEE:    { bg: '#fce7f3', text: '#9d174d' },
  COMMISSION:   { bg: '#d1fae5', text: '#065f46' },
};

function fmt(val) {
  return `$${Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
}

// ── Add Client Modal (3 steps: Org Details → Frameworks & Fees → Review) ────
const PARTNER_FRAMEWORK_PRICE = 149; // fallback only; canonical price comes from /api/partner/billing/pricing

function AddClientModal({ plans, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep]   = useState(1);
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState('');

  const [form, setForm] = useState({
    orgName: '', contactEmail: '', websiteUrl: '',
  });

  // selected frameworks in step 2: { [frameworkName]: auditFee string }
  const [fwFees, setFwFees] = useState({});

  // SOC 2 specific metadata captured when SOC 2 is selected
  // soc2Type: 'TYPE_1' | 'TYPE_2'
  // tscCodes: Set-like object with TSC shorthand codes (CC is always required)
  // periodMonths: only for TYPE_2 — 3/6/12
  const [soc2Type,     setSoc2Type]     = useState('TYPE_2');
  const [tscCodes,     setTscCodes]     = useState({ CC: true, A1: false, C1: false, PI: false, P: false });
  const [periodMonths, setPeriodMonths] = useState(6);

  // Single source for the partner per-framework price + framework list (/api/partner/billing/pricing).
  const [pricing, setPricing] = useState([]);
  useEffect(() => { axiosInstance.get('/partner/billing/pricing').then(r => setPricing(r.data || [])).catch(() => {}); }, []);
  const frameworkNames = pricing.length ? pricing.map(p => p.frameworkName) : FRAMEWORKS;
  const priceByName = Object.fromEntries(pricing.map(p => [p.frameworkName, Number(p.monthlyPrice)]));
  const partnerPrice = pricing.length ? Number(pricing[0].monthlyPrice) : PARTNER_FRAMEWORK_PRICE;
  const priceFor = (name) => priceByName[name] ?? partnerPrice;

  const toggleFw = (fw) => {
    setFwFees(prev => {
      if (fw in prev) { const n = { ...prev }; delete n[fw]; return n; }
      return { ...prev, [fw]: '' };
    });
  };

  const setFee = (fw, val) => setFwFees(prev => ({ ...prev, [fw]: val }));

  const isSoc2 = isSoc2Name;
  const tscSelected = () => Object.entries(tscCodes).filter(([, v]) => v).map(([k]) => k).join(',');

  const buildEngagements = () =>
    Object.entries(fwFees).map(([frameworkName, auditFee]) => {
      const base = {
        frameworkName,
        auditFee: parseFloat(auditFee || '0') || 0,
      };
      // Attach SOC 2 metadata only for SOC 2 engagements
      if (isSoc2(frameworkName)) {
        base.auditType         = soc2Type === 'TYPE_1' ? 'SOC2_TYPE_1' : 'SOC2_TYPE_2';
        base.tscSelection      = tscSelected();
        base.auditPeriodMonths = soc2Type === 'TYPE_2' ? Number(periodMonths) : null;
      }
      return base;
    });

  const stepValid = () => {
    if (step === 1) return form.orgName.trim().length > 0;
    if (step === 2) return true;
    if (step === 3) return true;
    return false;
  };

  const fwCount = Object.keys(fwFees).length;
  const monthlyPartnerBilling = Object.keys(fwFees).reduce((s, n) => s + priceFor(n), 0);
  const totalAuditFees = Object.values(fwFees).reduce((s, v) => s + (parseFloat(v || '0') || 0), 0);

  const handleSubmit = async () => {
    setSaving(true); setErr('');
    try {
      await axiosInstance.post('/partner/cpa/clients', {
        orgName: form.orgName,
        contactEmail: form.contactEmail,
        websiteUrl: form.websiteUrl,
        planId: null,            // CPA partner billing — no end-client subscription
        billingCycle: 'MONTHLY',
        engagements: buildEngagements(),
      });
      onSuccess();
    } catch (e) {
      setErr(e?.response?.data?.message || t('CpaDashboard.errCreateClient'));
    } finally { setSaving(false); }
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="cpa-modal-wide mp-modal" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <div>
            <h3>{t('CpaDashboard.addClientTitle')}</h3>
            <div className="cpa-step-indicator">
              {[t('CpaDashboard.stepOrgDetails'), t('CpaDashboard.stepFrameworksFees'), t('CpaDashboard.stepReview')].map((label, i) => (
                <span key={i} className={`cpa-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                  <span className="cpa-step-num">{step > i + 1 ? '✓' : i + 1}</span>
                  {label}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        <div className="mp-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {err && <div className="mp-msg mp-msg-err">{err}</div>}

          {/* Step 1: Org Details */}
          {step === 1 && (
            <div className="cpa-step-body">
              <p className="cpa-step-desc">{t('CpaDashboard.step1Desc')}</p>
              <div className="mp-field">
                <label>{t('CpaDashboard.orgNameLabel')}</label>
                <input className="mp-input" value={form.orgName}
                  onChange={e => setForm(f => ({ ...f, orgName: e.target.value }))}
                  placeholder={t('CpaDashboard.orgNamePlaceholder')} autoFocus />
              </div>
              <div className="mp-field">
                <label>{t('CpaDashboard.contactEmailLabel')}</label>
                <input className="mp-input" type="email" value={form.contactEmail}
                  onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
                  placeholder="admin@acme.com" />
              </div>
              <div className="mp-field">
                <label>{t('CpaDashboard.websiteUrlLabel')}</label>
                <input className="mp-input" value={form.websiteUrl}
                  onChange={e => setForm(f => ({ ...f, websiteUrl: e.target.value }))}
                  placeholder="https://acme.com" />
              </div>
            </div>
          )}

          {/* Step 2: Frameworks & Audit Fees */}
          {step === 2 && (
            <div className="cpa-step-body">
              <p className="cpa-step-desc">
                {t('CpaDashboard.step2Desc')}
              </p>
              <div className="cpa-fw-list">
                {frameworkNames.map(fw => {
                  const checked = fw in fwFees;
                  return (
                    <div key={fw} className={`cpa-fw-row ${checked ? 'cpa-fw-selected' : ''}`}>
                      <label className="cpa-fw-check-label">
                        <input type="checkbox" checked={checked} onChange={() => toggleFw(fw)} />
                        <span className="cpa-fw-name">{fw}</span>
                      </label>
                      {checked && (
                        <div className="cpa-fw-fee">
                          <span className="cpa-fee-prefix">$</span>
                          <input className="mp-input cpa-fee-input" type="number" min="0" step="100"
                            value={fwFees[fw]}
                            onChange={e => setFee(fw, e.target.value)}
                            placeholder="0" />
                          <span className="cpa-fee-suffix">{t('CpaDashboard.auditFeeSuffix')}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* SOC 2-specific picker — only when SOC 2 is selected */}
              {('SOC 2' in fwFees) && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12,
                  padding: 16, marginTop: 16 }}>
                  <div style={{ fontWeight: 800, color: '#0c4a6e', marginBottom: 12,
                    display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="fas fa-shield-halved" /> {t('CpaDashboard.soc2EngagementDetails')}
                  </div>

                  {/* Type 1 / Type 2 toggle */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: '0.78rem', color: '#075985', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('CpaDashboard.auditTypeLabel')}</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {[
                        { v: 'TYPE_1', label: 'SOC 2 Type 1', hint: t('CpaDashboard.soc2Type1Hint') },
                        { v: 'TYPE_2', label: 'SOC 2 Type 2', hint: t('CpaDashboard.soc2Type2Hint') },
                      ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => setSoc2Type(opt.v)}
                          style={{ flex: 1, textAlign: 'left', padding: '10px 12px',
                            border: soc2Type === opt.v ? '2px solid #0ea5e9' : '2px solid #e2e8f0',
                            background: soc2Type === opt.v ? '#e0f2fe' : '#fff', borderRadius: 10,
                            cursor: 'pointer', transition: 'all 0.15s' }}>
                          <div style={{ fontWeight: 700, color: '#0c4a6e' }}>
                            <i className={`far ${soc2Type === opt.v ? 'fa-dot-circle' : 'fa-circle'}`} style={{ marginRight: 6 }} />
                            {opt.label}
                          </div>
                          <div style={{ fontSize: '0.74rem', color: '#475569', marginTop: 3 }}>{opt.hint}</div>
                        </button>
                      ))}
                    </div>
                    {soc2Type === 'TYPE_2' && (
                      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '0.82rem', color: '#075985', fontWeight: 600 }}>{t('CpaDashboard.periodLabel')}</span>
                        {[3, 6, 12].map(m => (
                          <button key={m} type="button" onClick={() => setPeriodMonths(m)}
                            style={{ padding: '4px 12px', borderRadius: 16,
                              border: periodMonths === m ? '2px solid #0ea5e9' : '1px solid #cbd5e1',
                              background: periodMonths === m ? '#e0f2fe' : '#fff',
                              fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                              color: periodMonths === m ? '#0c4a6e' : '#475569' }}>
                            {t('CpaDashboard.monthsCount', { count: m })}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Trust Service Criteria selector */}
                  <div>
                    <div style={{ fontSize: '0.78rem', color: '#075985', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                      {t('CpaDashboard.tscTitle')}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                      {[
                        { code: 'CC', label: t('CpaDashboard.tscCCLabel'), required: true,  hint: t('CpaDashboard.tscCCHint') },
                        { code: 'A1', label: t('CpaDashboard.tscA1Label'), required: false, hint: t('CpaDashboard.tscA1Hint') },
                        { code: 'C1', label: t('CpaDashboard.tscC1Label'), required: false, hint: t('CpaDashboard.tscC1Hint') },
                        { code: 'PI', label: t('CpaDashboard.tscPILabel'), required: false, hint: t('CpaDashboard.tscPIHint') },
                        { code: 'P',  label: t('CpaDashboard.tscPLabel'),  required: false, hint: t('CpaDashboard.tscPHint') },
                      ].map(crit => {
                        const on = !!tscCodes[crit.code];
                        return (
                          <label key={crit.code}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 8,
                              padding: '8px 10px', borderRadius: 8,
                              border: on ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                              background: on ? '#e0f2fe' : '#fff',
                              cursor: crit.required ? 'not-allowed' : 'pointer',
                              opacity: crit.required ? 0.85 : 1 }}>
                            <input type="checkbox" checked={on} disabled={crit.required}
                              onChange={e => setTscCodes(p => ({ ...p, [crit.code]: e.target.checked }))}
                              style={{ marginTop: 2 }} />
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0c4a6e' }}>
                                {crit.label}
                                {crit.required && <span style={{ marginLeft: 6, fontSize: '0.66rem', color: '#dc2626', fontWeight: 700 }}>{t('CpaDashboard.requiredBadge')}</span>}
                              </div>
                              <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 2 }}>{crit.hint}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {Object.keys(fwFees).length > 0 && (
                <div className="cpa-fee-total" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>{t('CpaDashboard.yourMonthlyPartnerBilling')}</div>
                    <div style={{ fontWeight: 800, color: '#0c4a6e', fontSize: '1.1rem' }}>
                      {t('CpaDashboard.perMonthValue', { value: fmt(monthlyPartnerBilling) })}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {t('CpaDashboard.frameworkRateBreakdown', { count: Object.keys(fwFees).length })}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>{t('CpaDashboard.clientAuditFeesCollect')}</div>
                    <div style={{ fontWeight: 800, color: '#7c2d12', fontSize: '1.1rem' }}>
                      {fmt(Object.values(fwFees).reduce((s, v) => s + (parseFloat(v || '0') || 0), 0))}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('CpaDashboard.oneTimeAuditTotal')}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Review — Partner billing summary ($99/framework/month) */}
          {step === 3 && (
            <div className="cpa-step-body">
              <p className="cpa-step-desc">
                {t('CpaDashboard.step3Desc')}
              </p>

              {/* Pricing summary box */}
              <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '1px solid #38bdf8', borderRadius: 12, padding: 20, marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <i className="fas fa-receipt" style={{ fontSize: 22, color: '#0369a1' }} />
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0c4a6e' }}>
                    {t('CpaDashboard.partnerBillingSummary')}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 16px', fontSize: '0.9rem' }}>
                  <div style={{ color: '#075985' }}>{t('CpaDashboard.frameworksEnrolled')}</div>
                  <div style={{ fontWeight: 700, color: '#0c4a6e' }}>{fwCount}</div>

                  <div style={{ color: '#075985' }}>{t('CpaDashboard.partnerRate')}</div>
                  <div style={{ fontWeight: 700, color: '#0c4a6e' }}>{t('CpaDashboard.partnerRateValue')}</div>

                  <div style={{ gridColumn: '1 / 3', borderTop: '1px solid #bae6fd', margin: '6px 0' }} />

                  <div style={{ fontWeight: 700, color: '#0c4a6e' }}>{t('CpaDashboard.monthlyPartnerBilling')}</div>
                  <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0c4a6e' }}>
                    {t('CpaDashboard.perMonthValue', { value: fmt(monthlyPartnerBilling) })}
                  </div>

                  <div style={{ color: '#075985', fontSize: '0.83rem' }}>{t('CpaDashboard.annualEquivalent')}</div>
                  <div style={{ color: '#075985', fontSize: '0.83rem' }}>{t('CpaDashboard.perYearValue', { value: fmt(monthlyPartnerBilling * 12) })}</div>
                </div>

                {totalAuditFees > 0 && (
                  <div style={{ marginTop: 14, padding: '10px 12px', background: '#fff7ed',
                    borderRadius: 8, fontSize: '0.83rem', color: '#9a3412' }}>
                    <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
                    {t('CpaDashboard.clientInvoicedPrefix')} <strong>{fmt(totalAuditFees)}</strong> {t('CpaDashboard.clientInvoicedSuffix')}
                  </div>
                )}
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 14,
                fontSize: '0.85rem', color: '#14532d', display: 'flex', gap: 10 }}>
                <i className="fas fa-gift" style={{ fontSize: 18, color: '#16a34a', marginTop: 2 }} />
                <div>
                  <strong>{t('CpaDashboard.freeCreditsBold')}</strong> {t('CpaDashboard.freeCreditsRest')}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mp-modal-footer">
          {step > 1 && (
            <button className="mp-btn-secondary" onClick={() => setStep(s => s - 1)}>
              <i className="fas fa-arrow-left" /> {t('CpaDashboard.back')}
            </button>
          )}
          <button className="mp-btn-secondary" onClick={onClose} style={{ marginLeft: step === 1 ? 'auto' : undefined }}>
            {t('CpaDashboard.cancel')}
          </button>
          {step < 3 ? (
            <button className="mp-btn-primary" disabled={!stepValid()} onClick={() => setStep(s => s + 1)}>
              {t('CpaDashboard.next')} <i className="fas fa-arrow-right" />
            </button>
          ) : (
            <button className="mp-btn-primary" disabled={saving} onClick={handleSubmit}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaDashboard.adding')}</> : <><i className="fas fa-plus" /> {t('CpaDashboard.addClient')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Manage Frameworks Modal ────────────────────────────────────────────────
function ManageFrameworksModal({ org, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [currentFws, setCurrentFws] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');
  const [newFw,      setNewFw]      = useState('');
  const [newFee,     setNewFee]     = useState('');
  const [pricing,    setPricing]    = useState([]);

  useEffect(() => {
    Promise.all([
      axiosInstance.get(`/partner/cpa/orgs/${org.id}/frameworks`),
      axiosInstance.get('/partner/billing/pricing'),
    ])
      .then(([fwRes, priceRes]) => { setCurrentFws(fwRes.data || []); setPricing(priceRes.data || []); })
      .catch(() => setCurrentFws([]))
      .finally(() => setLoading(false));
  }, [org.id]);

  const currentFwNames = currentFws.map(f => f.display_name || f.name);
  // Dropdown sourced from live framework pricing so new frameworks appear automatically.
  const availableFws = (pricing.length ? pricing.map(p => p.frameworkName) : FRAMEWORKS).filter(fw => !currentFwNames.includes(fw));

  const handleAdd = async () => {
    if (!newFw) { setErr(t('CpaDashboard.errSelectFramework')); return; }
    setSaving(true); setErr('');
    try {
      await axiosInstance.post(`/partner/cpa/orgs/${org.id}/frameworks`, {
        frameworkName: newFw,
        auditFee: parseFloat(newFee || '0') || 0,
      });
      onSuccess(newFw);
    } catch (e) {
      setErr(e?.response?.data?.message || t('CpaDashboard.errAddFramework'));
      setSaving(false);
    }
  };

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="mp-modal cpa-fw-modal" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <div>
            <h3><i className="fas fa-layer-group" /> {t('CpaDashboard.manageFrameworks')}</h3>
            <p className="cpa-modal-sub">{org.name}</p>
          </div>
          <button onClick={onClose}><i className="fas fa-times" /></button>
        </div>
        <div className="mp-modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {err && <div className="mp-msg mp-msg-err">{err}</div>}
          {loading ? (
            <div className="cpa-fw-loading"><i className="fas fa-spinner fa-spin" /> {t('CpaDashboard.loadingEllipsis')}</div>
          ) : (
            <>
              <div className="cpa-fw-current-section">
                <div className="cpa-fw-section-label">{t('CpaDashboard.enrolledFrameworksCount', { count: currentFwNames.length })}</div>
                {currentFwNames.length === 0 ? (
                  <p className="mp-empty" style={{ margin: '8px 0', fontSize: '0.85rem' }}>
                    {t('CpaDashboard.noFrameworksEnrolled')}
                  </p>
                ) : (
                  <div className="cpa-fw-tag-list">
                    {currentFwNames.map(fw => (
                      <span key={fw} className="cpa-fw-tag">{fw}</span>
                    ))}
                  </div>
                )}
              </div>

              {availableFws.length > 0 && (
                <div className="cpa-fw-add-section">
                  <div className="cpa-fw-section-label">{t('CpaDashboard.addFramework')}</div>
                  <div className="mp-field">
                    <label>{t('CpaDashboard.frameworkLabel')}</label>
                    <select className="mp-input" value={newFw} onChange={e => setNewFw(e.target.value)}>
                      <option value="">{t('CpaDashboard.selectFrameworkOption')}</option>
                      {availableFws.map(fw => <option key={fw} value={fw}>{fw}</option>)}
                    </select>
                  </div>
                  <div className="mp-field">
                    <label>{t('CpaDashboard.auditFeeLabel')}</label>
                    <input className="mp-input" type="number" min="0" step="100"
                      value={newFee} onChange={e => setNewFee(e.target.value)}
                      placeholder="0" />
                  </div>
                  <div className="cpa-billing-notice">
                    <i className="fas fa-info-circle" /> {t('CpaDashboard.addFrameworkBillingNotice')}
                  </div>
                </div>
              )}
              {availableFws.length === 0 && currentFwNames.length > 0 && (
                <div className="cpa-billing-notice" style={{ marginTop: 12 }}>
                  <i className="fas fa-check-circle" /> {t('CpaDashboard.allFrameworksEnrolled')}
                </div>
              )}
            </>
          )}
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-secondary" onClick={onClose}>{t('CpaDashboard.close')}</button>
          {availableFws.length > 0 && (
            <button className="mp-btn-primary" onClick={handleAdd}
              disabled={saving || !newFw || loading}>
              {saving
                ? <><i className="fas fa-spinner fa-spin" /> {t('CpaDashboard.addingEllipsis')}</>
                : <><i className="fas fa-plus" /> {t('CpaDashboard.addFramework')}</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main CpaDashboard ──────────────────────────────────────────────────────

export default function CpaDashboard() {
  const { t } = useTranslation();
  const [profile, setProfile]   = useState(null);
  const [orgs, setOrgs]         = useState([]);
  // brand config is stored only in brandForm; no separate brand state needed
  const [ledger, setLedger]     = useState(null);
  const [plans, setPlans]       = useState([]);
  const [tab, setTab]           = useState('clients');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [showAddClient,      setShowAddClient]      = useState(false);
  const [manageFwOrg,        setManageFwOrg]        = useState(null);
  const [workspaceOrg,       setWorkspaceOrg]       = useState(null);
  const [workspaceTab,       setWorkspaceTab]       = useState('integrations');
  const [brandForm, setBrandForm] = useState({
    primaryColor: '#1e3a5f', accentColor: '#3b82f6',
    customDomain: '', reportFooterText: '', logoUrl: ''
  });

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      axiosInstance.get('/partner/reseller/profile'),
      axiosInstance.get('/partner/reseller/organizations'),
      axiosInstance.get('/partner/cpa/brand-config'),
      axiosInstance.get('/partner/cpa/ledger'),
      axiosInstance.get('/billing/plans'),
    ]).then(([pRes, oRes, bRes, lRes, plRes]) => {
      if (pRes.status === 'fulfilled') setProfile(pRes.value.data);
      if (oRes.status === 'fulfilled') setOrgs(oRes.value.data);
      if (bRes.status === 'fulfilled') {
        const b = bRes.value.data;
        setBrandForm({
          primaryColor: b.primaryColor || '#1e3a5f',
          accentColor:  b.accentColor  || '#3b82f6',
          customDomain: b.customDomain || '',
          reportFooterText: b.reportFooterText || '',
          logoUrl: b.logoUrl || '',
        });
      }
      if (lRes.status === 'fulfilled') setLedger(lRes.value.data);
      if (plRes.status === 'fulfilled') setPlans(plRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Re-open the client workspace if returning from the vendor setup wizard
  useEffect(() => {
    const hashSearch = window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '';
    const qp = new URLSearchParams(hashSearch);
    const returnId = qp.get('cpaClientReturn');
    if (returnId && orgs.length > 0) {
      const target = orgs.find(o => String(o.id) === String(returnId));
      if (target) {
        setWorkspaceOrg(target);
        setWorkspaceTab('integrations');
        sessionStorage.removeItem('cpaClientContext');
        // Strip the query param so refresh doesn't reopen
        window.location.hash = '/dashboard/marketplace';
      }
    }
  }, [orgs]);

  const handleSaveBrand = async () => {
    setSaving(true); setMsg('');
    try {
      await axiosInstance.put('/partner/cpa/brand-config', brandForm);
      setMsg(t('CpaDashboard.brandSaved'));
      fetchAll();
    } catch { setMsg(t('CpaDashboard.brandSaveFailed')); }
    finally { setSaving(false); }
  };

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsg(t('CpaDashboard.logoTooLarge')); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setBrandForm(f => ({ ...f, logoUrl: ev.target.result }));
    reader.readAsDataURL(file);
  };

  if (loading) return <div className="mp-loading"><i className="fas fa-spinner fa-spin" /> {t('CpaDashboard.loadingPortal')}</div>;

  const l = ledger || {};

  return (
    <div className="mp-wrap">
      <div className="mp-header">
        <div>
          <h2 className="mp-title"><i className="fas fa-balance-scale" /> {t('CpaDashboard.portalTitle')}</h2>
          {profile && <p className="mp-subtitle">{t('CpaDashboard.firmSubtitle', { firmName: profile.firmName })}</p>}
        </div>
      </div>

      {msg && <div className={`mp-msg ${msg.includes('Failed') ? 'mp-msg-err' : ''}`}>{msg}</div>}

      <div className="mp-kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('CpaDashboard.kpiClientOrgs')}</div>
          <div className="mp-kpi-value">{orgs.length}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('CpaDashboard.kpiAuditFeesBilled')}</div>
          <div className="mp-kpi-value" style={{ color: '#1e40af' }}>{fmt(l.totalAuditFees)}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('CpaDashboard.kpiCommissionEarned')}</div>
          <div className="mp-kpi-value" style={{ color: '#065f46' }}>{fmt(l.commissionEarned)}</div>
        </div>
        <div className="mp-kpi">
          <div className="mp-kpi-label">{t('CpaDashboard.kpiOutstanding')}</div>
          <div className="mp-kpi-value" style={{ color: l.totalPending > 0 ? '#92400e' : '#64748b' }}>
            {fmt(l.totalPending)}
          </div>
        </div>
      </div>

      <div className="mp-tabs">
        {[['clients',      'fa-building',        t('CpaDashboard.tabClients', { count: orgs.length })],
          ['alerts',       'fa-shield-alt',      t('CpaDashboard.tabCommandCenter')],
          ['remediation',  'fa-tasks',           t('CpaDashboard.tabRemediation')],
          ['ledger',       'fa-chart-line',      t('CpaDashboard.tabFinancialLedger')],
          ['branding',     'fa-palette',         t('CpaDashboard.tabWhiteLabel')],
          ['profile',      'fa-id-card',         t('CpaDashboard.tabFirmProfile')],
        ].map(([key, icon, label]) => (
          <button key={key} className={`mp-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            <i className={`fas ${icon}`} /> {label}
          </button>
        ))}
      </div>

      {/* ── Clients Tab ─────────────────────────────────────────── */}
      {tab === 'clients' && (
        <div className="mp-section">
          <div className="cpa-section-header">
            <h3 className="mp-section-title" style={{ margin: 0 }}>{t('CpaDashboard.clientOrgsTitle')}</h3>
            <button className="mp-btn-primary" onClick={() => setShowAddClient(true)}>
              <i className="fas fa-plus" /> {t('CpaDashboard.addClient')}
            </button>
          </div>
          {orgs.length === 0 ? (
            <div className="mp-empty">
              {t('CpaDashboard.noClientOrgs')}
            </div>
          ) : (
            <div className="mp-client-grid">
              {orgs.map(o => (
                <div key={o.id} className="mp-client-card">
                  <div className="mp-client-logo">
                    {o.logoUrl
                      ? <img src={o.logoUrl} alt="" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 6 }} />
                      : <div className="mp-org-avatar mp-org-avatar-lg">{(o.name || '?')[0].toUpperCase()}</div>}
                  </div>
                  <div className="mp-client-name">{o.name}</div>
                  <div className="mp-client-email">{o.contactEmail || '—'}</div>
                  <div className="cpa-client-meta">
                    {o.invoiceCount > 0 && (
                      <span className="cpa-inv-badge">{t('CpaDashboard.invoiceCount', { count: o.invoiceCount })}</span>
                    )}
                  </div>
                  {(o.frameworks || []).length > 0 && (
                    <div className="cpa-client-fw-tags">
                      {o.frameworks.map(fw => (
                        <span key={fw} className="cpa-fw-tag cpa-fw-tag-sm">{fw}</span>
                      ))}
                    </div>
                  )}
                  <div className="mp-client-actions">
                    <button className="mp-btn-sm" onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('asm'); }}
                      title={t('CpaDashboard.asmTitle')}>
                      <i className="fas fa-globe" /> ASM
                    </button>
                    <button className="mp-btn-sm" onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('iasm'); }}
                      title={t('CpaDashboard.iasmTitle')}>
                      <i className="fas fa-network-wired" /> iASM
                    </button>
                    <button className="mp-btn-sm" onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('audits'); }}>
                      <i className="fas fa-clipboard-check" /> {t('CpaDashboard.audits')}
                    </button>
                    <button className="mp-btn-sm mp-btn-sm-outline"
                      onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('frameworks'); }}>
                      <i className="fas fa-layer-group" /> {t('CpaDashboard.frameworks')}
                      {(o.frameworkCount || 0) > 0 && (
                        <span className="cpa-fw-count-badge">{o.frameworkCount}</span>
                      )}
                    </button>
                    <button className="mp-btn-sm mp-btn-sm-outline"
                      onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('team'); }}>
                      <i className="fas fa-users" /> {t('CpaDashboard.team')}
                    </button>
                    <button className="mp-btn-sm mp-btn-sm-outline"
                      onClick={() => { setWorkspaceOrg(o); setWorkspaceTab('integrations'); }}>
                      <i className="fas fa-plug" /> {t('CpaDashboard.more')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Command Center Tab ──────────────────────────────────── */}
      {tab === 'alerts' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('CpaDashboard.securityCommandCenter')}</h3>
          <p className="mp-section-desc" style={{ marginBottom: 16 }}>
            {t('CpaDashboard.commandCenterDesc')}
          </p>
          <CommandCenter />
        </div>
      )}

      {/* ── Remediation Tab ─────────────────────────────────────── */}
      {tab === 'remediation' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('CpaDashboard.remediationProjects')}</h3>
          <p className="mp-section-desc" style={{ marginBottom: 16 }}>
            {t('CpaDashboard.remediationDesc')}
          </p>
          <RemediationDashboard />
        </div>
      )}

      {/* ── Financial Ledger Tab ─────────────────────────────────── */}
      {tab === 'ledger' && (
        <div>
          {/* Partner Billing banner ($99/framework/month) */}
          {(() => {
            const activeFwCount = (l.engagements || []).filter(e => !e.status || e.status === 'ACTIVE').length;
            const monthlyPartnerCost = activeFwCount * PARTNER_FRAMEWORK_PRICE;
            return (
              <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                border: '1px solid #38bdf8', borderRadius: 12, padding: '16px 20px', marginBottom: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <i className="fas fa-receipt" style={{ fontSize: 26, color: '#0369a1' }} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0c4a6e' }}>
                      {t('CpaDashboard.partnerBillingPostPaid')}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#075985', marginTop: 2 }}>
                      {t('CpaDashboard.partnerChargePrefix')} <strong>{t('CpaDashboard.partnerChargeRate')}</strong> {t('CpaDashboard.partnerChargeSuffix', { count: activeFwCount })}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.78rem', color: '#075985', fontWeight: 600 }}>{t('CpaDashboard.thisMonth')}</div>
                  <div style={{ fontWeight: 800, fontSize: '1.5rem', color: '#0c4a6e' }}>{fmt(monthlyPartnerCost)}</div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{t('CpaDashboard.perYearValue', { value: fmt(monthlyPartnerCost * 12) })}</div>
                </div>
              </div>
            );
          })()}

          {/* Summary cards */}
          <div className="cpa-ledger-summary">
            {[
              { label: t('CpaDashboard.cardAuditFees'),            value: fmt(l.totalAuditFees),           icon: 'fa-file-invoice-dollar', color: '#9d174d', bg: '#fce7f3' },
              { label: t('CpaDashboard.cardCommission', { rate: l.commissionRate }), value: fmt(l.commissionEarned), icon: 'fa-percentage', color: '#065f46', bg: '#d1fae5' },
              { label: t('CpaDashboard.cardTotalCollected'),       value: fmt(l.totalPaid),               icon: 'fa-check-circle', color: '#047857', bg: '#ecfdf5' },
              { label: t('CpaDashboard.cardOutstanding'),           value: fmt(l.totalPending),            icon: 'fa-clock',        color: '#92400e', bg: '#fef3c7' },
            ].map(card => (
              <div key={card.label} className="cpa-ledger-card">
                <div className="cpa-ledger-icon" style={{ color: card.color, background: card.bg }}>
                  <i className={`fas ${card.icon}`} />
                </div>
                <div>
                  <div className="cpa-ledger-val">{card.value}</div>
                  <div className="cpa-ledger-lbl">{card.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Audit Engagements */}
          {(l.engagements || []).length > 0 && (
            <div className="mp-section" style={{ marginBottom: 16 }}>
              <h3 className="mp-section-title">{t('CpaDashboard.auditEngagements')}</h3>
              <table className="mp-table">
                <thead>
                  <tr><th>{t('CpaDashboard.thOrganization')}</th><th>{t('CpaDashboard.thFramework')}</th><th>{t('CpaDashboard.thAuditFee')}</th><th>{t('CpaDashboard.thStatus')}</th><th>{t('CpaDashboard.thCreated')}</th></tr>
                </thead>
                <tbody>
                  {(l.engagements || []).map((e, i) => (
                    <tr key={i}>
                      <td><span className="mp-org-name">{e.organization_name}</span></td>
                      <td><span className="cpa-fw-tag">{e.framework_name}</span></td>
                      <td style={{ fontWeight: 600 }}>{fmt(e.audit_fee)}</td>
                      <td>
                        <span className="cpa-status-pill cpa-status-active">
                          {e.status || 'ACTIVE'}
                        </span>
                      </td>
                      <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                        {e.created_at ? new Date(e.created_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Invoice Ledger */}
          <div className="mp-section">
            <h3 className="mp-section-title">{t('CpaDashboard.invoiceLedger')}</h3>
            {(l.invoices || []).length === 0 ? (
              <div className="mp-empty">{t('CpaDashboard.noInvoices')}</div>
            ) : (
              <table className="mp-table">
                <thead>
                  <tr><th>{t('CpaDashboard.thOrganization')}</th><th>{t('CpaDashboard.thDescription')}</th><th>{t('CpaDashboard.thType')}</th><th>{t('CpaDashboard.thAmount')}</th><th>{t('CpaDashboard.thStatus')}</th><th>{t('CpaDashboard.thDue')}</th><th>{t('CpaDashboard.thPaid')}</th></tr>
                </thead>
                <tbody>
                  {(l.invoices || []).map((inv, i) => {
                    const sc  = INV_STATUS_COLORS[inv.status] || INV_STATUS_COLORS.PENDING;
                    const svc = SVC_COLORS[inv.service_type]  || SVC_COLORS.SUBSCRIPTION;
                    return (
                      <tr key={i}>
                        <td><span className="mp-org-name">{inv.organization_name}</span></td>
                        <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{inv.description || '—'}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                            background: svc.bg, color: svc.text }}>
                            {SVC_LABELS[inv.service_type] || inv.service_type}
                          </span>
                        </td>
                        <td style={{ fontWeight: 600 }}>{fmt(inv.total_amount)}</td>
                        <td>
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                            background: sc.bg, color: sc.text }}>{inv.status}</span>
                        </td>
                        <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                          {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                          {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── White-label Branding Tab ─────────────────────────────── */}
      {tab === 'branding' && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('CpaDashboard.whiteLabelConfig')}</h3>
          <p className="mp-section-desc">
            {t('CpaDashboard.whiteLabelDesc')}
          </p>
          <div className="mp-brand-preview" style={{ borderLeft: `4px solid ${brandForm.primaryColor}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {brandForm.logoUrl
                ? <img src={brandForm.logoUrl} alt={t('CpaDashboard.logoAlt')} style={{ height: 48, objectFit: 'contain', borderRadius: 6 }} />
                : <div className="mp-brand-logo-placeholder" style={{ background: brandForm.primaryColor }}>
                    <i className="fas fa-building" />
                  </div>}
              <div>
                <div style={{ fontWeight: 700, color: brandForm.primaryColor, fontSize: 16 }}>
                  {profile?.firmName || t('CpaDashboard.yourFirmName')}
                </div>
                <div style={{ color: brandForm.accentColor, fontSize: 12 }}>{t('CpaDashboard.complianceAuditReport')}</div>
              </div>
            </div>
          </div>
          <div className="mp-form-grid">
            <div className="mp-field">
              <label>{t('CpaDashboard.firmLogoLabel')}</label>
              <input type="file" accept="image/*" onChange={handleLogoFile} className="mp-file-input" />
              {brandForm.logoUrl && (
                <div style={{ marginTop: 8 }}>
                  <img src={brandForm.logoUrl} alt={t('CpaDashboard.previewAlt')} style={{ height: 40, objectFit: 'contain', borderRadius: 4 }} />
                </div>
              )}
            </div>
            <div className="mp-field">
              <label>{t('CpaDashboard.primaryColorLabel')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={brandForm.primaryColor}
                  onChange={e => setBrandForm(f => ({ ...f, primaryColor: e.target.value }))} />
                <input className="mp-input" value={brandForm.primaryColor}
                  onChange={e => setBrandForm(f => ({ ...f, primaryColor: e.target.value }))}
                  style={{ width: 100 }} />
              </div>
            </div>
            <div className="mp-field">
              <label>{t('CpaDashboard.accentColorLabel')}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="color" value={brandForm.accentColor}
                  onChange={e => setBrandForm(f => ({ ...f, accentColor: e.target.value }))} />
                <input className="mp-input" value={brandForm.accentColor}
                  onChange={e => setBrandForm(f => ({ ...f, accentColor: e.target.value }))}
                  style={{ width: 100 }} />
              </div>
            </div>
            <div className="mp-field">
              <label>{t('CpaDashboard.customDomainLabel')}</label>
              <input className="mp-input" value={brandForm.customDomain}
                onChange={e => setBrandForm(f => ({ ...f, customDomain: e.target.value }))}
                placeholder="audit.yourfirm.com" />
            </div>
            <div className="mp-field mp-field-full">
              <label>{t('CpaDashboard.reportFooterLabel')}</label>
              <textarea className="mp-input" rows={2} value={brandForm.reportFooterText}
                onChange={e => setBrandForm(f => ({ ...f, reportFooterText: e.target.value }))}
                placeholder={t('CpaDashboard.reportFooterPlaceholder')} />
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <button className="mp-btn-primary" onClick={handleSaveBrand} disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> {t('CpaDashboard.saving')}</> : <><i className="fas fa-save" /> {t('CpaDashboard.saveBranding')}</>}
            </button>
          </div>
        </div>
      )}

      {/* ── Firm Profile Tab ─────────────────────────────────────── */}
      {tab === 'profile' && profile && (
        <div className="mp-section">
          <h3 className="mp-section-title">{t('CpaDashboard.firmProfile')}</h3>
          <div className="mp-profile-grid">
            {[[t('CpaDashboard.firmNameField'), profile.firmName], [t('CpaDashboard.contactEmailField'), profile.contactEmail],
              [t('CpaDashboard.websiteField'), profile.websiteUrl], [t('CpaDashboard.commissionRateField'), `${profile.commissionRate}%`],
              [t('CpaDashboard.statusField'), profile.status], [t('CpaDashboard.stripeAccountField'), profile.stripeAccountId || t('CpaDashboard.notConnected')],
            ].map(([k, v]) => (
              <div key={k} className="mp-profile-row">
                <span className="mp-profile-label">{k}</span>
                <span className="mp-profile-value">{v || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddClient && (
        <AddClientModal
          plans={plans}
          onClose={() => setShowAddClient(false)}
          onSuccess={() => { setShowAddClient(false); setMsg(t('CpaDashboard.clientAddedSuccess')); fetchAll(); }}
        />
      )}

      {manageFwOrg && (
        <ManageFrameworksModal
          org={manageFwOrg}
          onClose={() => setManageFwOrg(null)}
          onSuccess={(fwName) => {
            setManageFwOrg(null);
            setMsg(t('CpaDashboard.frameworkAddedSuccess', { fwName, orgName: manageFwOrg.name }));
            fetchAll();
          }}
        />
      )}
      {workspaceOrg && (
        <CpaClientWorkspace
          org={workspaceOrg}
          initialTab={workspaceTab}
          onClose={() => setWorkspaceOrg(null)}
        />
      )}
    </div>
  );
}
